/**
 * CreatorLockModal — modal dla admina do włączania/wyłączania Creator Lock.
 *
 * Otwierany z menu avatara. Widoczny tylko dla admina (client-side gate w
 * `TopHeader.jsx`; server-side gate w `POST /api/creator-lock/toggle` — 403).
 *
 * Po zmianie: toast + zamknięcie modala. Bez restartu (backend ma cache 3s
 * i invalidację po write).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorLockModal = ({ open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  // Fetch aktualnego stanu na mount modala
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/creator-lock/status`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setEnabled(!!r.data?.enabled);
        setMeta({
          updated_at: r.data?.updated_at ?? null,
          updated_by: r.data?.updated_by ?? null,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(
          e?.response?.data?.detail || "Nie udało się pobrać stanu Creator Lock."
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/creator-lock/toggle`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(
        enabled
          ? "Creator Lock włączony — dostęp tylko dla administratora."
          : "Creator Lock wyłączony — aplikacja publicznie dostępna."
      );
      setMeta({
        updated_at: r.data?.updated_at ?? null,
        updated_by: r.data?.updated_by ?? null,
      });
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Nie udało się zapisać zmiany."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="creator-lock-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldAlert className="w-5 h-5 text-amber-500" />
            Creator Lock
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm text-gray-600">
            Blokuje publiczny dostęp do aplikacji. Administrator nadal może
            korzystać z systemu.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div
            className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
            data-testid="creator-lock-toggle-row"
          >
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">
                Stan Creator Lock
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {enabled
                  ? "Blokada aktywna — publiczny dostęp odcięty."
                  : "Blokada wyłączona — aplikacja dostępna publicznie."}
              </div>
            </div>
            <div className="flex items-center gap-3 ml-4">
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${
                  enabled ? "text-amber-600" : "text-gray-400"
                }`}
                data-testid="creator-lock-state-label"
              >
                {enabled ? "ON" : "OFF"}
              </span>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                disabled={loading || saving}
                data-testid="creator-lock-switch"
              />
            </div>
          </div>

          {meta.updated_at && (
            <div
              className="text-[11px] text-gray-500 flex items-center gap-2"
              data-testid="creator-lock-meta"
            >
              <Lock className="w-3 h-3" />
              Ostatnia zmiana:{" "}
              <span className="font-mono text-gray-700">
                {new Date(meta.updated_at).toLocaleString("pl-PL")}
              </span>
              {meta.updated_by && (
                <>
                  {" "}
                  przez <span className="font-mono">{meta.updated_by}</span>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="creator-lock-cancel-btn"
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-[#0F1F3D] hover:bg-[#1a2f5d] text-white"
            data-testid="creator-lock-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : null}
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreatorLockModal;
